import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from '../../animations/gsap.config';
import servicesData from '../../data/services.json';
import { 
  ArrowRight, 
  Download, 
  MessageCircle, 
  Calendar,
  CheckCircle,
  Sparkles,
  Users,
  Target,
  Award
} from 'lucide-react';

const ServicesCTA = () => {
  const { cta } = servicesData;
  const sectionRef = useRef(null);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const floatingCardsRef = useRef(null);
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      icon: Target,
      title: "Strategic Approach",
      description: "Evidence-based solutions tailored to your specific context and objectives"
    },
    {
      icon: Users,
      title: "Expert Team",
      description: "Experienced professionals with deep knowledge of development sectors"
    },
    {
      icon: Award,
      title: "Proven Results",
      description: "Track record of successful projects across Ethiopia and beyond"
    }
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Initial setup
      gsap.set([leftPanelRef.current, rightPanelRef.current], { opacity: 0 });
      
      // Main animation timeline
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 70%',
          end: 'bottom 30%',
        }
      });

      // Animate panels in
      tl.to(leftPanelRef.current, {
        opacity: 1,
        x: 0,
        duration: 1,
        ease: 'power3.out'
      })
      .to(rightPanelRef.current, {
        opacity: 1,
        x: 0,
        duration: 1,
        ease: 'power3.out'
      }, '-=0.7')
      .fromTo(floatingCardsRef.current.children, {
        y: 30,
        opacity: 0,
        scale: 0.9
      }, {
        y: 0,
        opacity: 1,
        scale: 1,
        stagger: 0.1,
        duration: 0.6,
        ease: 'back.out(1.2)'
      }, '-=0.5');

      // Floating animation for cards
      gsap.to(floatingCardsRef.current.children, {
        y: -10,
        duration: 2,
        ease: 'power2.inOut',
        stagger: 0.2,
        repeat: -1,
        yoyo: true
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section ref={sectionRef} className="relative py-20 md:py-32 overflow-hidden">
      {/* Dynamic Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50" />
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-conic from-blue-100/30 via-transparent to-indigo-100/30 rounded-full blur-2xl animate-spin" style={{ animationDuration: '20s' }} />
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[600px]">
          
          {/* Left Panel - Content */}
          <div ref={leftPanelRef} className="space-y-8">
            <div className="space-y-6">
             
              <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl text-slate-900 leading-tight">
                {cta.headline}{' '}
                <span className="bg-gradient-to-r from-blue-600 via-blue-600 to-blue-600 bg-clip-text text-transparent">
                  {cta.headlineAccent}
                </span>
              </h2>
              
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-lg">
                {cta.description}
              </p>
            </div>

            {/* Interactive Features */}
            <div className="space-y-4">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                const isActive = activeFeature === index;
                
                return (
                  <div
                    key={index}
                    className={`p-4 rounded-2xl border-2 transition-all duration-500 cursor-pointer ${
                      isActive 
                        ? 'border-blue-200 bg-blue-50/50 shadow-lg shadow-blue-100/50' 
                        : 'border-slate-200 bg-white/50 hover:border-slate-300'
                    }`}
                    onMouseEnter={() => setActiveFeature(index)}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-xl transition-colors duration-300 ${
                        isActive ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
                      }`}>
                        <Icon size={20} />
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-bold text-lg mb-1 transition-colors duration-300 ${
                          isActive ? 'text-blue-900' : 'text-slate-800'
                        }`}>
                          {feature.title}
                        </h3>
                        <p className="text-slate-600 text-sm leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link 
                to={cta.primaryCta.href}
                className="group relative inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-2xl transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-500/25 overflow-hidden"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                <MessageCircle size={20} />
                <span>{cta.primaryCta.text}</span>
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              
              <a 
                href={cta.secondaryCta.href}
                className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-white border-2 border-slate-200 hover:border-slate-300 text-slate-700 hover:text-slate-900 font-bold rounded-2xl transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <Download size={18} className="group-hover:animate-bounce" />
                <span>{cta.secondaryCta.text}</span>
              </a>
            </div>
          </div>

          {/* Right Panel - Visual Elements */}
          <div ref={rightPanelRef} className="relative">
            <div className="relative">
              {/* Main Card */}
              <div className="relative bg-white rounded-3xl p-8 shadow-2xl shadow-slate-200/50 border border-slate-100">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 rounded-3xl" />
                
                <div className="relative space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center">
                      <Calendar size={24} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-xl text-slate-900">Schedule a Consultation</h3>
                      <p className="text-slate-600">Free 30-minute discovery call</p>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-4 py-6 border-t border-slate-100">
                    {cta.stats.map((stat, i) => (
                      <div key={i} className="text-center">
                        <div className="text-2xl md:text-3xl font-black bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-1">
                          {stat.value}
                        </div>
                        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          {stat.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Process Steps */}
                  <div className="space-y-3">
                    <h4 className="font-bold text-slate-900 mb-3">What to Expect:</h4>
                    {[
                      "Discuss your project requirements",
                      "Explore tailored solutions",
                      "Receive detailed proposal"
                    ].map((step, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <CheckCircle size={14} className="text-green-600" />
                        </div>
                        <span className="text-slate-700 text-sm">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating Cards */}
              <div ref={floatingCardsRef} className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-4 -right-4 bg-gradient-to-br from-amber-400 to-orange-500 text-white p-4 rounded-2xl shadow-lg">
                  <Award size={24} />
                </div>
                
                <div className="absolute -bottom-4 -left-4 bg-gradient-to-br from-emerald-400 to-teal-500 text-white p-4 rounded-2xl shadow-lg">
                  <Users size={24} />
                </div>
                
                <div className="absolute top-1/2 -right-8 transform -translate-y-1/2 bg-gradient-to-br from-purple-400 to-pink-500 text-white p-3 rounded-xl shadow-lg">
                  <Target size={20} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesCTA;
